import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
} from '@mui/material';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { useUserContext } from '../../../contexts/UserContext';
import { updateUserSchema } from '../../../validation/schemas/updateUserSchema';
import api from '../../../services/api';
import Withdrawal from './Withdrawal/Withdrawal';
import showTableData from './Functions/showTableData';
import FormStyle from './FormStyle';
import ChangeForm from './EditUserInfo/ChangeForm';
import ChangeEmailForm from './ChangeEmail/ChangeForm';

interface UpdateUser {
  id: number;
  name: string;
  surname: string;
  nameKana: string;
  surnameKana: string;
  postCode: string;
  stateCity: string;
  streetBanNumber: number;
  buildingNumber: string;
  phone: string;
  email: string;
}

function MyAccountIndex() {
  // context
  const { user } = useUserContext();
  const [displayUser, setDisplayUser] = useState<UpdateUser>({
    id: 0,
    name: '',
    surname: '',
    nameKana: '',
    surnameKana: '',
    postCode: '',
    stateCity: '',
    streetBanNumber: 0,
    buildingNumber: '',
    phone: '',
    email: '',
  });

  // variables
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const notRegisteredMessage = 'Unregistered';

  // form
  const {
    register: addFormValues,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<UpdateUser>({
    mode: 'onSubmit',
    resolver: yupResolver(updateUserSchema),
    criteriaMode: 'all',
    defaultValues: {
      id: user.id,
      name: user.name,
      surname: user.surname,
      nameKana: '',
      surnameKana: '',
      postCode: '',
      stateCity: '',
      streetBanNumber: 0,
      buildingNumber: '',
      phone: '',
      email: user.mail,
    },
  });

  // functions
  async function getUser() {
    await api.get('/v1/users/me')
      .then((response) => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        setDisplayUser(response.data.data);
        setValue('name', response.data.data.name, { shouldValidate: true });
        setValue('surname', response.data.data.surname, { shouldValidate: true });
        setValue('nameKana', response.data.data.nameKana, { shouldValidate: true });
        setValue('surnameKana', response.data.data.surnameKana, { shouldValidate: true });
        setValue('phone', response.data.data.phone, { shouldValidate: true });
        setValue('postCode', response.data.data.postCode, { shouldValidate: true });
        setValue('stateCity', response.data.data.stateCity, { shouldValidate: true });
        setValue('streetBanNumber', response.data.data.streetBanNumber, { shouldValidate: true });
        setValue('buildingNumber', response.data.data.buildingNumber, { shouldValidate: true });
        setValue('email', response.data.data.mail, { shouldValidate: true });
      });
  }

  const handleWithdrawal = () => {
    setIsWithdrawal(true);
  };

  const handleChangeEmailConfirmation = () => {
    setIsChangingEmail(true);
  };

  const handleEditProfileState = () => {
    setIsLoading(true);
    getUser().then(() => {
      setIsEditing(!isEditing);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  // effects
  useEffect(() => {
    setIsLoading(true);
    getUser()
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // render conditions
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isEditing) {
    return (
      <ChangeForm
        addFormValues={addFormValues}
        handleSubmit={handleSubmit}
        setIsEditing={setIsEditing}
        errors={errors}
        getUser={() => getUser()}
      />
    );
  }

  if (isChangingEmail) {
    return (
      <ChangeEmailForm
        setIsChangingEmail={setIsChangingEmail}
        getValues={getValues}
        addFormValues={addFormValues}
        errors={errors}
      />
    );
  }

  if (isWithdrawal) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Withdrawal />
      </Box>
    );
  }

  return (
    <div className="main">
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          className="container-fluid py-5"
        >
          <Box sx={FormStyle.previewHeader}>
            <h1 className="text-bold">Profile</h1>
            Check and change your registered profile information
          </Box>

          <Box>
            <Box className="card">
              <Box className="card-body">
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <Table className="table-form-setting">
                      <TableBody>
                        {showTableData({
                          ticket: 'Contract plan',
                          value: (user.myPlan?.name),
                        })}
                        {showTableData({
                          ticket: 'Name',
                          value: `${displayUser.surname ?? ''} ${displayUser.name ?? ''}`,
                        })}
                        {showTableData({
                          ticket: 'Name (furigana)',
                          value: (displayUser.nameKana || displayUser.surnameKana) ? `${displayUser.surnameKana ?? ''} ${displayUser.nameKana ?? ''}` : notRegisteredMessage,
                        })}
                        {showTableData({
                          ticket: 'Post code',
                          value: (displayUser.postCode) ?? notRegisteredMessage,
                        })}
                        {showTableData({
                          ticket: 'Prefecture/city/ward/town/village',
                          value: (displayUser.stateCity) ?? notRegisteredMessage,
                        })}
                        {showTableData({
                          ticket: 'Chome and number',
                          value: (displayUser.streetBanNumber) ? String(displayUser.streetBanNumber) : notRegisteredMessage,
                        })}
                        {showTableData({
                          ticket: 'Building name and room number',
                          value: (displayUser.buildingNumber) ? displayUser.buildingNumber : notRegisteredMessage,
                        })}
                        {showTableData({
                          ticket: 'Telephone number',
                          value: (displayUser.phone) ? displayUser.phone : notRegisteredMessage,
                        })}
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={handleEditProfileState}
                      variant="contained"
                    >
                      Edit your profile
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    marginTop={5}
                  >
                    <Table className="table-form-setting">
                      <TableBody>
                        {showTableData({
                          ticket: 'Email address',
                          value: (user.mail ?? notRegisteredMessage),
                        })}
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid
                    container
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Button
                      onClick={handleChangeEmailConfirmation}
                      variant="contained"
                    >
                      Change your email address
                    </Button>
                    <hr />
                    <Button
                      variant="contained"
                      className="btn-danger"
                      onClick={handleWithdrawal}
                    >
                      Cancel your membership
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Box>
      </main>
    </div>
  );
}

export default MyAccountIndex;
